import { useQuery } from "@tanstack/react-query";
import { Bell, Clock3, CreditCard } from "lucide-react";
import { useState } from "react";
import { toast } from "sonner";
import { useAppContext } from "@/app/providers/AppContext";
import { useTheme } from "@/app/providers/ThemeContext";
import { apiRequest } from "@/shared/lib/apiClient";
import type { ApiFn, Profile } from "@/shared/lib/types";
import { FONT_DISPLAY, FONT_MONO, FONT_SANS } from "@/shared/lib/typography";
import { Mono, Pill, StatDot, T } from "@/shared/ui/DesignLib";
import { Button } from "@/shared/ui/button";
import {
    Dialog,
    DialogContent,
    DialogDescription,
    DialogHeader,
    DialogTitle,
} from "@/shared/ui/dialog";

interface PricingCardPlan {
  planId: string;
  tier: string;
  name: string;
  price: string;
  priceStyle?: React.CSSProperties;
  freq: string;
  tagline: string;
  features: string[];
  featureClass: "light" | "dark" | "white";
  dotPattern: string | null;
  cta: string;
  ctaStyle: React.CSSProperties;
  ctaHover: React.CSSProperties;
  cardBg: string;
  cardBorder: string;
  dark: boolean;
}

interface CheckoutResponse {
  checkout_url?: string;
}

interface BillingStatusResponse {
  status?: string;
  current_period_end?: string;
  payment_method?: { last4: string; brand: string } | null;
}

interface PlanApiResponse {
  slug: string;
  display_name?: string;
  price_monthly?: number;
  max_active_watches?: number;
  max_watch_duration_days?: number;
  eval_interval_minutes?: number;
}

interface PlansTabProps {
  profile: Profile | null;
  api: ApiFn;
}

function errorMessage(error: unknown, fallback: string): string {
  return error instanceof Error ? error.message : fallback;
}

const PlansTab = ({ profile, api }: PlansTabProps) => {
  const { loadProfile, updateProfilePlan } = useAppContext();
  const { theme } = useTheme();
  const [isLoading, setIsLoading] = useState(false);
  const [activePlanId, setActivePlanId] = useState<string | null>(null);
  const [showToolboxDowngradeConfirm, setShowToolboxDowngradeConfirm] =
    useState(false);

  const currentPlan = profile?.plan?.toLowerCase() || "trial";

  const { data: billingStatus } = useQuery<BillingStatusResponse>({
    queryKey: ["billing-status"],
    queryFn: () => apiRequest<BillingStatusResponse>("GET", "/api/billing/status"),
    enabled: Boolean(profile?.has_billing_account),
    staleTime: 2 * 60_000,
  });

  const { data: livePlans } = useQuery<PlanApiResponse[]>({
    queryKey: ["billing-plans"],
    queryFn: () => apiRequest<PlanApiResponse[]>("GET", "/api/billing/plans"),
    staleTime: 10 * 60_000,
  });

  const getLiveQuota = (planId: string, key: keyof PlanApiResponse): number | undefined => {
    const match = livePlans?.find((p) => p.slug === planId);
    return match ? (match[key] as number | undefined) : undefined;
  };

  const isDarkTheme = theme === "dark";
  const toolboxCardBg = isDarkTheme ? "var(--card)" : "#ffffff";
  const toolboxCardBorder = isDarkTheme ? "var(--border)" : "#e5e5e5";
  const sharedCtaStyle = isDarkTheme
    ? { background: "var(--foreground)", color: "var(--background)" }
    : { background: "#0d0d0d", color: "white" };
  const sharedCtaHover = { background: "#00B8DB", color: "white" };

  const plans: PricingCardPlan[] = [
    {
      planId: "toolbox",
      tier: "Toolbox",
      name: "Toolbox",
      price: "$18",
      freq: "per month",
      tagline: "Monthly access to LinkedHash inside Claude.",
      features: [
        "Access to all current + future tools",
        "Run any playbooks and workflows on demand",
        "0 active watches, no automated alerts",
        "Upgrade to Oracle anytime",
        "Monthly billing, cancel anytime",
      ],
      featureClass: "light",
      dotPattern: isDarkTheme
        ? "rgba(255,255,255,0.12)"
        : "rgba(0,184,219,0.15)",
      cta: "Upgrade",
      ctaStyle: sharedCtaStyle,
      ctaHover: sharedCtaHover,
      cardBg: toolboxCardBg,
      cardBorder: toolboxCardBorder,
      dark: false,
    },
    {
      planId: "oracle",
      tier: "Oracle",
      name: "Oracle",
      price: "$29",
      freq: "per month",
      tagline: "Add automated watches and alerts.",
      features: [
        "Everything in Toolbox",
        "10 active watches",
        "30-day max watch duration",
        "5-minute evaluation intervals",
        "20 alerts per watch",
        "Email support, typically under 48 hours",
      ],
      featureClass: "light",
      dotPattern: isDarkTheme
        ? "rgba(255,255,255,0.12)"
        : "rgba(0,184,219,0.15)",
      cta: "Upgrade",
      ctaStyle: sharedCtaStyle,
      ctaHover: sharedCtaHover,
      cardBg: toolboxCardBg,
      cardBorder: toolboxCardBorder,
      dark: false,
    },
    {
      planId: "oracle_monitor",
      tier: "Oracle Monitor",
      name: "Monitor",
      price: "$79",
      freq: "per month",
      tagline: "For desks that need continuous monitoring.",
      features: [
        "Everything in Oracle",
        "50 active watches",
        "30-day max watch duration",
        "5-minute evaluation intervals",
        "20 alerts per watch",
        "Continuous monitoring when offline",
        "Priority email support, under 24 hours",
      ],
      featureClass: "light",
      dotPattern: isDarkTheme
        ? "rgba(255,255,255,0.12)"
        : "rgba(0,184,219,0.15)",
      cta: "Upgrade",
      ctaStyle: sharedCtaStyle,
      ctaHover: sharedCtaHover,
      cardBg: toolboxCardBg,
      cardBorder: toolboxCardBorder,
      dark: false,
    },
  ];

  // F2 — override quota feature strings with live API data when available
  const resolvedPlans = plans.map((plan) => {
    const watches = getLiveQuota(plan.planId, "max_active_watches");
    const duration = getLiveQuota(plan.planId, "max_watch_duration_days");
    const interval = getLiveQuota(plan.planId, "eval_interval_minutes");
    if (!watches && !duration && !interval) return plan;
    return {
      ...plan,
      features: plan.features.map((f) => {
        if (watches !== undefined && /active watch/i.test(f)) return `${watches} active watches`;
        if (duration !== undefined && /max watch duration/i.test(f)) return `${duration}-day max watch duration`;
        if (interval !== undefined && /evaluation interval/i.test(f)) return `${interval}-minute evaluation intervals`;
        return f;
      }),
    };
  });

  const proceedPlanAction = async (
    planId: string,
    confirmToolboxDowngrade: boolean,
  ) => {
    setActivePlanId(planId);
    setIsLoading(true);
    try {
      const response = await api<CheckoutResponse>("POST", "/api/billing/checkout", {
        plan: planId,
        confirm_toolbox_downgrade: confirmToolboxDowngrade,
      });

      if (response.checkout_url) {
        window.location.href = response.checkout_url;
        return true;
      } else {
        // Reflect plan change instantly in UI, then reconcile from backend.
        updateProfilePlan(planId);
        toast.success("Plan updated successfully");
        setTimeout(() => {
          loadProfile(true);
        }, 2000);
        setTimeout(() => {
          loadProfile(true);
        }, 5000);
        return true;
      }
    } catch (e: unknown) {
      toast.error(errorMessage(e, "Failed to update plan"));
      return false;
    } finally {
      setIsLoading(false);
      setActivePlanId(null);
    }
  };

  const handlePlanAction = async (planId: string) => {
    if (planId === currentPlan) {
      toast.info("Already on this plan");
      return;
    }

    const isMonthlyToToolbox =
      planId === "toolbox" &&
      (currentPlan === "oracle" || currentPlan === "oracle_monitor");
    if (isMonthlyToToolbox) {
      setShowToolboxDowngradeConfirm(true);
      return;
    }

    await proceedPlanAction(planId, false);
  };

  const getTrialDaysRemaining = () => {
    if (currentPlan === "trial" && profile?.trial_info) {
      return profile.trial_info.trial_days_remaining || 0;
    }
    return 0;
  };

  const trialDaysRemaining = getTrialDaysRemaining();
  const currentPlanLabel = profile?.plan?.toUpperCase() || "TRIAL";
  const textColor = (_plan: PricingCardPlan) =>
    isDarkTheme ? "var(--foreground)" : "#1a1a1a";
  const subColor = (_plan: PricingCardPlan) =>
    isDarkTheme ? "var(--muted-foreground)" : "#999";
  const midColor = (_plan: PricingCardPlan) =>
    isDarkTheme ? "var(--muted-foreground)" : "#666";
  const borderB = (_plan: PricingCardPlan) =>
    isDarkTheme ? "var(--border)" : "#e5e5e5";
  const featColor = (_plan: PricingCardPlan) =>
    isDarkTheme ? "var(--muted-foreground)" : "#666";

  return (
    <div
      style={{ display: "flex", flexDirection: "column", gap: 18 }}
      className="fade-up"
    >
      <div
        style={{
          position: "relative",
          overflow: "hidden",
          border: `1px solid ${T.borderSoft}`,
          borderRadius: 12,
          background: `linear-gradient(135deg, ${T.primary}10, ${T.card} 42%, ${T.card2} 100%)`,
          boxShadow: "0 8px 20px rgba(0,0,0,0.08)",
        }}
      >
        <div
          style={{
            position: "absolute",
            inset: 0,
            pointerEvents: "none",
            background:
              "radial-gradient(circle at top right, rgba(0,184,219,0.16), transparent 30%), radial-gradient(circle at bottom left, rgba(255,255,255,0.05), transparent 36%)",
          }}
        />

        <div
          style={{
            position: "relative",
            display: "grid",
            gap: 16,
            padding: 18,
            alignItems: "center",
            gridTemplateColumns: "repeat(auto-fit, minmax(240px, 1fr))",
          }}
        >
          <div>
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 8,
                marginBottom: 8,
              }}
            >
              <Pill
                active
                color={T.primary}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <StatDot active />
                Billing
              </Pill>
              <Mono
                size=".65rem"
                color={T.dim}
                style={{ textTransform: "uppercase", letterSpacing: ".12em" }}
              >
                Subscription controls
              </Mono>
            </div>
            <div
              style={{
                fontFamily: T.serif,
                fontSize: "1.32rem",
                fontWeight: 700,
                color: T.text,
                lineHeight: 1.15,
              }}
            >
              Pricing Plans & Billing
            </div>
            <Mono
              size=".75rem"
              color={T.dim}
              style={{
                display: "block",
                marginTop: 6,
                lineHeight: 1.45,
                maxWidth: 500,
              }}
            >
              Compare plans, see what is active now, and move up or down without
              losing your place.
            </Mono>
          </div>

          {/* F3 — Billing Status card */}
          {profile?.has_billing_account && billingStatus && (
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 12,
                padding: "10px 14px",
                borderRadius: 10,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${T.borderSoft}`,
                marginBottom: 4,
              }}
            >
              <CreditCard size={15} color={T.primary} style={{ flexShrink: 0 }} />
              <div style={{ display: "flex", gap: 20, flexWrap: "wrap", flex: 1 }}>
                <div>
                  <Mono size=".58rem" color={T.dim} style={{ textTransform: "uppercase" }}>Subscription</Mono>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: ".82rem", fontWeight: 700, color: T.text, marginTop: 2, textTransform: "capitalize" }}>
                    {billingStatus.status ?? "—"}
                  </div>
                </div>
                {billingStatus.current_period_end && (
                  <div>
                    <Mono size=".58rem" color={T.dim} style={{ textTransform: "uppercase" }}>Renews</Mono>
                    <div style={{ fontFamily: FONT_DISPLAY, fontSize: ".82rem", fontWeight: 700, color: T.text, marginTop: 2 }}>
                      {new Intl.DateTimeFormat("en-GB", { day: "2-digit", month: "short", year: "numeric" }).format(new Date(billingStatus.current_period_end))}
                    </div>
                  </div>
                )}
                <div>
                  <Mono size=".58rem" color={T.dim} style={{ textTransform: "uppercase" }}>Payment</Mono>
                  <div style={{ fontFamily: FONT_DISPLAY, fontSize: ".82rem", fontWeight: 700, color: T.text, marginTop: 2 }}>
                    {billingStatus.payment_method ? `${billingStatus.payment_method.brand.toUpperCase()} ···· ${billingStatus.payment_method.last4}` : "None on file"}
                  </div>
                </div>
              </div>
            </div>
          )}

          <div
            style={{
              display: "grid",
              gridTemplateColumns:
                currentPlan === "trial"
                  ? "repeat(3, minmax(0, 1fr))"
                  : "repeat(2, minmax(0, 1fr))",
              gap: 8,
            }}
          >
            <div
              style={{
                padding: 10,
                borderRadius: 10,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${T.borderSoft}`,
              }}
            >
              <Mono
                size=".6rem"
                color={T.dim}
                style={{ textTransform: "uppercase" }}
              >
                Current plan
              </Mono>
              <div
                style={{
                  fontFamily: T.display,
                  fontSize: ".9rem",
                  fontWeight: 700,
                  color: T.text,
                  marginTop: 4,
                }}
              >
                {currentPlanLabel}
              </div>
            </div>
            <div
              style={{
                padding: 10,
                borderRadius: 10,
                background: "rgba(255,255,255,0.04)",
                border: `1px solid ${T.borderSoft}`,
              }}
            >
              <Mono
                size=".6rem"
                color={T.dim}
                style={{ textTransform: "uppercase" }}
              >
                Status
              </Mono>
              <div
                style={{
                  fontFamily: T.display,
                  fontSize: ".85rem",
                  fontWeight: 700,
                  color: T.text,
                  marginTop: 4,
                }}
              >
                Active
              </div>
            </div>
            {currentPlan === "trial" && (
              <div
                style={{
                  padding: 10,
                  borderRadius: 10,
                  background: "rgba(255,255,255,0.04)",
                  border: `1px solid ${T.borderSoft}`,
                }}
              >
                <Mono
                  size=".6rem"
                  color={T.dim}
                  style={{ textTransform: "uppercase" }}
                >
                  Next step
                </Mono>
                <div
                  style={{
                    fontFamily: T.display,
                    fontSize: ".85rem",
                    fontWeight: 700,
                    color: T.text,
                    marginTop: 4,
                  }}
                >
                  Upgrade anytime
                </div>
              </div>
            )}
          </div>
        </div>

        {currentPlan === "trial" && (
          <div
            style={{
              position: "relative",
              borderTop: `1px solid ${T.borderSoft}`,
              padding: "0 18px 14px",
            }}
          >
            <div
              style={{
                marginTop: -1,
                display: "flex",
                flexWrap: "wrap",
                gap: 10,
                alignItems: "center",
              }}
            >
              <Pill
                active
                color={trialDaysRemaining > 0 ? T.amber : T.red}
                style={{ display: "flex", alignItems: "center", gap: 6 }}
              >
                <Clock3 size={12} />
                {trialDaysRemaining > 0
                  ? `${trialDaysRemaining} days left`
                  : "Trial expired"}
              </Pill>
              <Mono size=".72rem" color={T.dim}>
                Choose a paid plan to keep watches, alerts, and billing active.
              </Mono>
            </div>
          </div>
        )}
      </div>

      <div
        style={{
          display: "grid",
          gridTemplateColumns: "repeat(auto-fit, minmax(260px, 1fr))",
          gap: 14,
        }}
      >
        {resolvedPlans.map((plan) => {
          const isCurrentPlan = plan.planId === currentPlan;
          const isCardLoading = isLoading && activePlanId === plan.planId;
          const buttonLabel = isCurrentPlan
            ? "Current Plan"
            : isCardLoading
              ? "Processing..."
              : plan.cta;

          return (
            <div
              key={plan.planId}
              style={{
                borderRadius: "16px",
                overflow: "hidden",
                border: `1px solid ${isCurrentPlan ? T.primary : plan.cardBorder}`,
                background: plan.cardBg,
                display: "flex",
                flexDirection: "column",
                transition: "transform 0.3s, box-shadow 0.3s",
                boxShadow: isCurrentPlan
                  ? "0 0 0 1px rgba(0,184,219,0.4), 0 14px 44px rgba(0,0,0,0.16)"
                  : undefined,
              }}
              onMouseEnter={(e) => {
                e.currentTarget.style.transform = "translateY(-4px)";
                e.currentTarget.style.boxShadow = isCurrentPlan
                  ? "0 0 0 1px rgba(0,184,219,0.4), 0 14px 44px rgba(0,0,0,0.16)"
                  : isDarkTheme
                    ? "0 14px 44px rgba(0,0,0,0.3)"
                    : "0 14px 44px rgba(0,0,0,0.09)";
              }}
              onMouseLeave={(e) => {
                e.currentTarget.style.transform = "";
                e.currentTarget.style.boxShadow = isCurrentPlan
                  ? "0 0 0 1px rgba(0,184,219,0.4), 0 14px 44px rgba(0,0,0,0.16)"
                  : "";
              }}
            >
              <div style={{ padding: "24px 24px 0" }}>
                <div
                  style={{
                    display: "flex",
                    justifyContent: "space-between",
                    alignItems: "flex-start",
                    gap: 8,
                  }}
                >
                  <div>
                    <div
                      style={{
                        fontFamily: FONT_SANS,
                        fontSize: "0.62rem",
                        letterSpacing: "0.12em",
                        textTransform: "uppercase",
                        color: subColor(plan),
                        marginBottom: "5px",
                      }}
                    >
                      {plan.tier}
                    </div>
                    <div
                      style={{
                        fontFamily: FONT_SANS,
                        fontSize: "1.3rem",
                        fontWeight: 700,
                        color: textColor(plan),
                        marginBottom: "4px",
                      }}
                    >
                      {plan.name}
                    </div>
                  </div>
                  {isCurrentPlan && (
                    <Pill
                      active
                      color={T.primary}
                      style={{ display: "flex", alignItems: "center", gap: 6 }}
                    >
                      <StatDot active />
                      Current
                    </Pill>
                  )}
                </div>
                <div
                  style={{
                    fontFamily: FONT_DISPLAY,
                    fontSize: "2rem",
                    color: T.primary,
                    lineHeight: 1,
                    marginBottom: "4px",
                    letterSpacing: "0.01em",
                    ...(plan.priceStyle || {}),
                  }}
                >
                  {plan.price}
                </div>
                <div
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: "0.58rem",
                    color: subColor(plan),
                    letterSpacing: "0.06em",
                    marginBottom: "10px",
                  }}
                >
                  {plan.freq}
                </div>
                <div
                  style={{
                    fontSize: "0.76rem",
                    color: midColor(plan),
                    fontStyle: "italic",
                    lineHeight: 1.55,
                    paddingBottom: "16px",
                    borderBottom: `1px solid ${borderB(plan)}`,
                  }}
                >
                  {plan.tagline}
                </div>
              </div>

              <div
                style={{
                  height: "80px",
                  backgroundImage: plan.dotPattern
                    ? `radial-gradient(circle, ${plan.dotPattern} 1px, transparent 1px)`
                    : undefined,
                  backgroundSize: "9px 9px",
                  maskImage:
                    "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%)",
                  WebkitMaskImage:
                    "linear-gradient(to bottom, transparent 0%, rgba(0,0,0,0.4) 100%)",
                }}
              />

              <div
                style={{
                  padding: "16px 24px",
                  flex: 1,
                  display: "flex",
                  flexDirection: "column",
                }}
              >
                <div
                  style={{
                    fontFamily: FONT_MONO,
                    fontSize: "0.55rem",
                    textTransform: "uppercase",
                    letterSpacing: "0.1em",
                    color: subColor(plan),
                    marginBottom: "10px",
                  }}
                >
                  Includes:
                </div>
                <ul
                  style={{
                    listStyle: "none",
                    display: "flex",
                    flexDirection: "column",
                    gap: "7px",
                    marginBottom: "16px",
                    flex: 1,
                  }}
                >
                  {plan.features.map((feat, j) => (
                    <li
                      key={j}
                      style={{
                        display: "flex",
                        alignItems: "flex-start",
                        gap: "7px",
                        fontSize: "0.74rem",
                        color: featColor(plan),
                      }}
                    >
                      <span
                        style={{
                          width: "13px",
                          height: "13px",
                          borderRadius: "50%",
                          flexShrink: 0,
                          marginTop: "1px",
                          background: "#00B8DB",
                          backgroundImage:
                            "url(\"data:image/svg+xml,%3Csvg viewBox='0 0 14 14' xmlns='http://www.w3.org/2000/svg'%3E%3Cpath d='M3 7l3 3 5-5' stroke='white' stroke-width='1.5' fill='none' stroke-linecap='round'/%3E%3C/svg%3E\")",
                          display: "inline-block",
                        }}
                      />
                      {feat}
                    </li>
                  ))}
                </ul>

                <button
                  onClick={() => {
                    if (isCurrentPlan || isLoading) return;
                    handlePlanAction(plan.planId);
                  }}
                  disabled={isCurrentPlan || isLoading}
                  style={{
                    width: "100%",
                    padding: "14px",
                    minHeight: "44px",
                    boxSizing: "border-box",
                    borderRadius: "100px",
                    fontFamily: FONT_SANS,
                    fontSize: "0.7rem",
                    letterSpacing: "0.05em",
                    cursor:
                      isCurrentPlan || isLoading ? "not-allowed" : "pointer",
                    border: "none",
                    marginTop: "auto",
                    transition: "all 0.2s",
                    opacity: isCurrentPlan || isLoading ? 0.65 : 1,
                    ...(isCurrentPlan
                      ? {
                          background: isDarkTheme
                            ? "rgba(255,255,255,0.14)"
                            : "rgba(0,0,0,0.08)",
                          color: textColor(plan),
                        }
                      : plan.ctaStyle),
                  }}
                  onMouseEnter={(e) => {
                    if (isCurrentPlan || isLoading) return;
                    Object.assign(e.currentTarget.style, plan.ctaHover);
                  }}
                  onMouseLeave={(e) => {
                    if (isCurrentPlan || isLoading) return;
                    Object.assign(e.currentTarget.style, plan.ctaStyle);
                  }}
                >
                  {buttonLabel}
                </button>
              </div>
            </div>
          );
        })}
      </div>

      <Dialog
        open={showToolboxDowngradeConfirm}
        onOpenChange={setShowToolboxDowngradeConfirm}
      >
        <DialogContent className="bg-background border border-border text-foreground max-w-[420px] p-0 overflow-hidden rounded-2xl shadow-2xl">
          <DialogHeader className="sr-only">
            <DialogTitle>Switch to Toolbox</DialogTitle>
            <DialogDescription>
              Confirm switching plans to Toolbox and acknowledge watch/alert
              limitations.
            </DialogDescription>
          </DialogHeader>
          <div
            style={{
              background: `linear-gradient(135deg, ${T.primary}20, transparent)`,
              padding: "28px 24px",
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 16,
                marginBottom: 16,
              }}
            >
              <div
                style={{
                  width: 36,
                  height: 36,
                  borderRadius: 10,
                  background: T.primary,
                  display: "grid",
                  placeItems: "center",
                  flexShrink: 0,
                }}
              >
                <Bell size={18} color="white" />
              </div>
              <h2
                style={{
                  fontFamily: T.serif,
                  fontSize: "1.25rem",
                  fontWeight: 700,
                  color: T.text,
                  margin: 0,
                  lineHeight: 1.2,
                }}
              >
                Switch to Toolbox?
              </h2>
            </div>
            <Mono
              size=".72rem"
              color={T.dim}
              className="leading-relaxed opacity-90"
            >
              Moving to Toolbox will remove all your active watches and disable
              alert preferences.
            </Mono>
          </div>

          <div style={{ padding: "0 24px 24px" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 12 }}>
              <Button
                className="flex-1 py-5 text-sm font-semibold"
                disabled={isLoading}
                onClick={async () => {
                  const ok = await proceedPlanAction("toolbox", true);
                  if (ok) setShowToolboxDowngradeConfirm(false);
                }}
              >
                {isLoading ? "Processing..." : "Continue to Toolbox"}
              </Button>
              <button
                onClick={() => setShowToolboxDowngradeConfirm(false)}
                disabled={isLoading}
                style={{
                  flexShrink: 0,
                  background: "rgba(255,255,255,0.03)",
                  border: `1px solid ${T.border}`,
                  color: T.dim,
                  cursor: "pointer",
                  fontFamily: T.mono,
                  fontSize: "0.65rem",
                  padding: "12px 16px",
                  borderRadius: 12,
                  transition: "all 0.15s",
                  opacity: isLoading ? 0.6 : 1,
                }}
                className="hover:text-foreground hover:border-muted-foreground"
              >
                Keep Current Plan
              </button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
};

export default PlansTab;
